import { ButtonKinds, Icon, Button, Modal, Text, DesignLanguage, useIsDesignLanguage, Theme, useIsTheme } from '@afterpaytouch/core'
import React, { FC, ReactNode, useMemo } from 'react'
import { useCheckoutSizes } from '../../utils/responsive'
import styles from './styles.module.css'
import { useTranslation } from '../../utils/convergence'
import { buildTranslationKey, TranslationSegment } from '../../utils/locales'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent, AutoPayModalKey, AutopayConfirmationModalKey } from '../../model'
import { Trans } from '../Trans'
import { PaymentAuthorizationLink } from '../PaymentAuthorizationLink'
import { createDocuments, useAppDispatch, useIsAfterpayFinanceFeeEligible, useIsApplePaylater, useIsConsumerLendingOrder } from '../../state'
import { TruthInLendingButton } from '../TruthInLending/TruthInLendingButton'
import { TruthInLendingButton as ApplePaylateTruthInLendingButton } from '../../flow/apple-paylater/components/TruthInLendingButton'

interface AutopayModalProps {
  show: boolean
  autoPayAgreed: boolean
  onCloseModal: () => void
  onAutopayStatusChange?: (value: boolean) => void
  modalKey?: string
}

export const AutopayModal: FC<AutopayModalProps> = ({ show, autoPayAgreed, onCloseModal, onAutopayStatusChange, modalKey = AutoPayModalKey }) => {
  const dispatch = useAppDispatch()
  const { headingSize, smallTextSize } = useCheckoutSizes()
  const isPaymentMonthly = useIsConsumerLendingOrder()
  const isApplePaylater = useIsApplePaylater()
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const { t } = useTranslation()
  const modelSegment: TranslationSegment = {
    contexts: [
      [isPaymentMonthly, 'modal_paymentMonthly'],
      [isAFF, 'modal_aff'],
    ],
    default: 'modal',
  }
  const ctaSegment: TranslationSegment = {
    contexts: [[isPaymentMonthly, 'cta']],
    default: 'cta',
  }
  const localeKey = buildTranslationKey({ namespace: 'summary', segments: ['autopay', modelSegment] })
  const modalMode = autoPayAgreed ? 'off' : 'on'
  const description = t(`${localeKey}.description`, { context: modalMode })
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isCashTheme = useIsTheme(Theme.Cash)
  // For the autopay summary page modal, it uses `Enable Autopay` and `Disable Autopay`
  // For the autopay confirmation modal, it uses `Confirm and continue` and `Disable Autopay` for A experiment
  // For the autopay confirmation modal, it uses `Confirm` and `Disable Autopay` for B experiment
  const ctaText = t(
    modalKey === AutoPayModalKey
      ? `summary:autopay.modal.cta`
      : `${buildTranslationKey({ namespace: 'summary', segments: ['autopay', modelSegment, ctaSegment] })}`,
    {
      context: modalMode,
    }
  )

  const heading = t(`${localeKey}.heading`, { context: modalMode })
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const ctaButtonKind: ButtonKinds = autoPayAgreed ? 'Error' : 'Primary'

  const eventName = useMemo(() => {
    if (isPaymentMonthly) {
      return autoPayAgreed ? TrackingEvent.VIEWED_AUTOPAY_OFF_PAGE : TrackingEvent.VIEWED_AUTOPAY_ON_PAGE
    }
    return TrackingEvent.VIEWED_AUTOPAY_HALF_SHEET
  }, [autoPayAgreed, isPaymentMonthly])

  const onCloseButtonClick = (): void => {
    onCloseModal()

    if (isPaymentMonthly) {
      logEvent(autoPayAgreed ? TrackingEvent.PRESSED_AUTOPAY_OFF_PAGE_CLOSE_BUTTON : TrackingEvent.PRESSED_AUTOPAY_ON_PAGE_CLOSE_BUTTON)
    } else {
      logEvent(TrackingEvent.PRESSED_SUMMARY_PAGE_AUTOPAY_CLOSE_BUTTON)
    }
  }

  const onCtaButtonClick = (): void => {
    const autopay = !autoPayAgreed

    if (isPaymentMonthly || isAFF) {
      dispatch(createDocuments({ autopay }))
    }
    onAutopayStatusChange?.(autopay)
    onCloseModal()
  }

  const components = {
    paymentAuthorization: <PaymentAuthorizationLink />,
  }

  const renderCtaButton = (): ReactNode => {
    if (modalKey === AutopayConfirmationModalKey) {
      if (isApplePaylater) {
        return <ApplePaylateTruthInLendingButton />
      }
      return <TruthInLendingButton />
    }

    return (
      <Button kind={ctaButtonKind} padding='Fluid' testNameSpace='autopay-cta' onClick={onCtaButtonClick}>
        {ctaText}
      </Button>
    )
  }

  return (
    <Modal id={modalKey} showCloseButton={isBronteDesignLanguage} onBackdropClick={onCloseModal} testNameSpace='Autopay' show={show}>
      <Modal.Header>
        <div className={styles.autoPayModalIcon}>
          {autoPayAgreed ? (
            isCashTheme ? (
              <Icon name='AlertFill' color='Error' size='Large' />
            ) : (
              <Icon name='RecurringAutomatic' color='Prominent' size='Large' />
            )
          ) : (
            <Icon name='RecurringAutomatic' color={isCashTheme ? 'Brand' : 'Prominent'} size='Large' />
          )}
        </div>
        <Text bold size={headingSize}>
          {heading}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <Text color='Subtle'>{description}</Text>
        {modalMode === 'on' && !isPaymentMonthly && !isAFF && (
          <p className={styles.autoPayAgreementSection}>
            <Text renderAs='span' color='Subtle' size={smallTextSize}>
              <Trans i18nKey={`${localeKey}.agreement`} components={components} />
            </Text>
          </p>
        )}
        <PaymentAuthorizationLink />
      </Modal.Content>
      <Modal.Footer>
        {renderCtaButton()}
        <Button onClick={onCloseButtonClick} kind='Secondary' padding='Fluid' testNameSpace='autopay-close'>
          {t(`${localeKey}.close`)}
        </Button>
      </Modal.Footer>
      <TrackOnMount eventName={eventName} />
    </Modal>
  )
}
